import { useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Plus, Sparkles, User } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Page } from "@/components/layout/page";
import { ActionBar } from "@/components/layout/action-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/empty-state";
import { EntityErrorState } from "@/components/layout/entity-state";
import { FilteredEmptyState } from "@/components/ui/filtered-empty-state";
import { GeneratePersonasModal } from "@/components/experience/generate-personas-modal";
import { useCanEdit, useEditorCopy } from "@/lib/project-access";
import type {
  PersonaListItem,
  PersonaSortKey,
  PersonaStatusFilter,
} from "./personas-tab-types";
import { PersonasToolbar } from "./personas-toolbar";
import { PersonasTable } from "./personas-table";
import { PersonaFormDialog } from "./persona-form-dialog";
import { PersonaDeleteDialog } from "./persona-delete-dialog";

interface PersonasTabProps {
  projectId: string;
}

export function PersonasTab({ projectId }: PersonasTabProps) {
  const { id: routeProjectId } = useParams<{ id: string }>();
  const resolvedProjectId = routeProjectId ?? projectId;

  const [showCreate, setShowCreate] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [editingPersona, setEditingPersona] = useState<PersonaListItem | null>(
    null,
  );

  const canEdit = useCanEdit();
  const emptyDescription = useEditorCopy(
    "Personas are domain-grounded characters that walk through scenarios. Create one manually or draft with your coding agent.",
    "Personas are domain-grounded characters that walk through scenarios. Ask an editor to add the first one.",
  );

  const {
    data: personasList,
    isLoading,
    error,
    refetch,
  } = trpc.persona.list.useQuery({ projectId });

  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") ?? "";
  const statusParam = searchParams.get("status");
  const statusFilter: PersonaStatusFilter =
    statusParam === "active" || statusParam === "archived"
      ? statusParam
      : "all";
  const sortParam = searchParams.get("sort");
  const sortKey: PersonaSortKey = sortParam === "recent" ? "recent" : "name";

  const handleSearchChange = (val: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (val.trim()) {
          next.set("search", val);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleStatusFilterChange = (status: PersonaStatusFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (status !== "all") {
          next.set("status", status);
        } else {
          next.delete("status");
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleSortChange = (key: PersonaSortKey) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key !== "name") {
          next.set("sort", key);
        } else {
          next.delete("sort");
        }
        return next;
      },
      { replace: true },
    );
  };

  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("search");
        next.delete("status");
        return next;
      },
      { replace: true },
    );
  };

  const hasActiveFilters =
    searchQuery.trim().length > 0 || statusFilter !== "all";

  const personas = useMemo(() => {
    if (!personasList) return [];
    const q = searchQuery.trim().toLowerCase();
    return personasList
      .filter((p) => {
        if (statusFilter !== "all" && (p.status ?? "active") !== statusFilter) {
          return false;
        }
        if (q) {
          const matchName = p.name?.toLowerCase().includes(q);
          const matchRole = p.role?.toLowerCase().includes(q);
          const matchBrief = p.brief?.toLowerCase().includes(q);
          if (!matchName && !matchRole && !matchBrief) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (sortKey === "name") {
          return a.name.localeCompare(b.name);
        }
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
  }, [personasList, searchQuery, statusFilter, sortKey]);

  const actions = canEdit ? (
    <ActionBar
      primary={
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New persona
        </Button>
      }
      secondary={[
        <Button key="agent" onClick={() => setShowGenerate(true)}>
          <Sparkles className="mr-2 h-4 w-4" />
          Draft with agent
        </Button>,
      ]}
    />
  ) : undefined;

  if (isLoading) {
    return (
      <Page title="Personas" actions={actions}>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Personas" actions={actions}>
        <EntityErrorState
          title="Failed to load personas"
          description="We couldn't retrieve the personas for this project. Please try again."
          onRetry={() => refetch()}
          embedded
        />
      </Page>
    );
  }

  return (
    <Page title="Personas" actions={actions}>
      {personasList && personasList.length > 0 && (
        <PersonasToolbar
          totalCount={personasList.length}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          sortKey={sortKey}
          onSortChange={handleSortChange}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={resetFilters}
        />
      )}

      {!personasList || personasList.length === 0 ? (
        <EmptyState
          icon={User}
          title="No personas yet"
          description={emptyDescription}
          actions={
            canEdit ? (
              <>
                <Button onClick={() => setShowCreate(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New persona
                </Button>
                <Button variant="outline" onClick={() => setShowGenerate(true)}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Draft with agent
                </Button>
              </>
            ) : undefined
          }
          variant="compact"
        />
      ) : personas.length === 0 ? (
        <FilteredEmptyState
          title="No personas match these filters"
          description="No personas match your active search query or status filter."
          onReset={resetFilters}
        />
      ) : (
        <PersonasTable
          personas={personas}
          resolvedProjectId={resolvedProjectId}
          canEdit={canEdit}
          onEdit={(persona) => setEditingPersona(persona)}
          onDelete={(p) => setShowDeleteConfirm(p)}
        />
      )}

      {/* Create / Edit Dialog */}
      <PersonaFormDialog
        projectId={projectId}
        open={showCreate || editingPersona !== null}
        onClose={() => {
          setShowCreate(false);
          setEditingPersona(null);
        }}
        editingPersona={editingPersona}
      />

      {/* Delete Confirmation Dialog */}
      <PersonaDeleteDialog
        projectId={projectId}
        persona={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
      />

      {/* Generate Modal */}
      {canEdit && showGenerate && (
        <GeneratePersonasModal
          projectId={projectId}
          onClose={() => setShowGenerate(false)}
        />
      )}
    </Page>
  );
}
